export type OnboardingCompletionKind = 'agency' | 'profile';

export type OnboardingCompletionSnapshot = {
  userId: string;
  kind: OnboardingCompletionKind;
  invitationsSent: boolean;
};

const ONBOARDING_COMPLETION_STORAGE_KEY = 'crm:onboarding:completion';

const isOnboardingCompletionKind = (
  kind: unknown,
): kind is OnboardingCompletionKind => kind === 'agency' || kind === 'profile';

const parseOnboardingCompletion = (
  value: unknown,
): OnboardingCompletionSnapshot | null => {
  if (value == null || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }

  const snapshot = value as Record<string, unknown>;
  const userId = snapshot.userId;
  const kind = snapshot.kind;
  const invitationsSent = snapshot.invitationsSent;

  if (typeof userId !== 'string' || userId.trim().length === 0) {
    return null;
  }
  if (!isOnboardingCompletionKind(kind)) {
    return null;
  }
  if (typeof invitationsSent !== 'boolean') {
    return null;
  }

  return {
    userId,
    kind,
    invitationsSent,
  };
};

export const readOnboardingCompletion =
  (): OnboardingCompletionSnapshot | null => {
    if (typeof window === 'undefined') {
      return null;
    }

    try {
      const rawSnapshot = window.localStorage.getItem(
        ONBOARDING_COMPLETION_STORAGE_KEY,
      );
      if (rawSnapshot == null) {
        return null;
      }
      return parseOnboardingCompletion(JSON.parse(rawSnapshot));
    } catch {
      return null;
    }
  };

export const writeOnboardingCompletion = (
  snapshot: OnboardingCompletionSnapshot,
): void => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.setItem(
      ONBOARDING_COMPLETION_STORAGE_KEY,
      JSON.stringify(snapshot),
    );
  } catch {
    // ignore
  }
};

export const clearOnboardingCompletion = (): void => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.removeItem(ONBOARDING_COMPLETION_STORAGE_KEY);
  } catch {
    // ignore
  }
};

export const readOnboardingCompletionForUser = (
  userId?: string | null,
): OnboardingCompletionSnapshot | null => {
  const snapshot = readOnboardingCompletion();
  if (snapshot == null || userId == null || userId.length === 0) {
    return null;
  }

  return snapshot.userId === userId ? snapshot : null;
};
